import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Trophy, 
  Target, 
  GraduationCap, 
  Gift, 
  Calendar, 
  MapPin,
  Download,
  FileText,
  Flame,
  Star,
  Crown,
  Award,
  ArrowLeft,
  Coins,
  TrendingUp,
  Zap
} from 'lucide-react';
import { PERMANENT_ACHIEVEMENTS, CYCLE_MILESTONES } from '../achievements/achievementDefinitions';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const IncentiveProgramDocument: React.FC = () => {
  const documentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleDownloadPDF = async () => {
    if (!documentRef.current) return;
    
    const canvas = await html2canvas(documentRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#0f172a'
    });
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save('VillaTech-Programa-Incentivos.pdf');
  };

  // Filter permanent achievements by category
  const permanentSales = PERMANENT_ACHIEVEMENTS.filter(a => a.category === 'ventas');
  const permanentRedemptions = PERMANENT_ACHIEVEMENTS.filter(a => a.category === 'canjes');
  const permanentSavings = PERMANENT_ACHIEVEMENTS.filter(a => a.category === 'ahorros');
  const permanentTraining = PERMANENT_ACHIEVEMENTS.filter(a => a.category === 'entrenamiento');

  // Filter cycle milestones by category
  const cycleSales = CYCLE_MILESTONES.filter(a => a.category === 'ventas');
  const cycleRedemptions = CYCLE_MILESTONES.filter(a => a.category === 'canjes');
  const cycleRanking = CYCLE_MILESTONES.filter(a => a.category === 'ranking');
  const cycleSavings = CYCLE_MILESTONES.filter(a => a.category === 'ahorros');
  const cycleSpecial = CYCLE_MILESTONES.filter(a => a.category === 'especial');

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-blue-950/20 to-slate-950 p-4 md:p-8">
      {/* Navigation Buttons */}
      <div className="max-w-4xl mx-auto mb-6 flex justify-between items-center">
        <Button 
          onClick={() => navigate(-1)}
          variant="ghost"
          className="text-white/70 hover:text-white hover:bg-white/10 gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        <Button 
          onClick={handleDownloadPDF}
          className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
        >
          <Download className="h-4 w-4" />
          Descargar PDF
        </Button>
      </div>

      {/* Document Content */}
      <div ref={documentRef} className="max-w-4xl mx-auto space-y-6 bg-slate-950 p-6 md:p-10 rounded-xl">
        
        {/* Header */}
        <div className="text-center space-y-4 pb-6 border-b border-blue-500/30">
          <div className="flex justify-center">
            <div className="bg-gradient-to-r from-blue-600 to-blue-400 p-4 rounded-xl">
              <FileText className="h-12 w-12 text-white" />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white">
            Programa de Incentivos VillaTech
          </h1>
          <p className="text-blue-400 text-lg">Reglas del Juego - {new Date().getFullYear()}</p>
          <Badge variant="outline" className="border-blue-500/50 text-blue-300">
            Documento Oficial
          </Badge>
        </div>

        {/* Section 1: NEW Scoring System */}
        <Card className="bg-gradient-to-br from-blue-900/60 to-blue-950/80 border-blue-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-blue-300">
              <Target className="h-6 w-6" />
              1. Sistema de Puntuación (Ranking)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-900/80 p-4 rounded-lg border border-blue-500/20">
              <p className="text-blue-300 font-mono text-center text-base md:text-lg leading-relaxed">
                Score = Membresías + Canjes + Pasivos + Ahorros + Hitos + Logros
              </p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-blue-500/30">
                    <th className="text-left py-3 text-blue-400">Categoría</th>
                    <th className="text-left py-3 text-blue-400">Descripción</th>
                    <th className="text-right py-3 text-blue-400">Puntos</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-300">
                  <tr className="border-b border-zinc-800">
                    <td className="py-3 font-medium text-amber-600">Membresía Bronce</td>
                    <td className="py-3 text-zinc-400">Por cada membresía Bronce vendida</td>
                    <td className="text-right text-green-400 font-semibold">+10 pts</td>
                  </tr>
                  <tr className="border-b border-zinc-800">
                    <td className="py-3 font-medium text-slate-400">Membresía Plata</td>
                    <td className="py-3 text-zinc-400">Por cada membresía Plata vendida</td>
                    <td className="text-right text-green-400 font-semibold">+20 pts</td>
                  </tr>
                  <tr className="border-b border-zinc-800">
                    <td className="py-3 font-medium text-yellow-400">Membresía Oro</td>
                    <td className="py-3 text-zinc-400">Por cada membresía Oro vendida</td>
                    <td className="text-right text-green-400 font-semibold">+40 pts</td>
                  </tr>
                  <tr className="border-b border-zinc-800">
                    <td className="py-3 font-medium text-blue-400">Canje Procesado</td>
                    <td className="py-3 text-zinc-400">Por procesar un canje de beneficio</td>
                    <td className="text-right text-green-400 font-semibold">+8 pts</td>
                  </tr>
                  <tr className="border-b border-zinc-800">
                    <td className="py-3 font-medium text-purple-400">Punto Pasivo</td>
                    <td className="py-3 text-zinc-400">Cuando otro procesa canje de tu venta</td>
                    <td className="text-right text-green-400 font-semibold">+2 pts</td>
                  </tr>
                  <tr className="border-b border-zinc-800">
                    <td className="py-3 font-medium text-emerald-400">Ahorros Generados</td>
                    <td className="py-3 text-zinc-400">Por cada ₡10,000 en ahorros para clientes</td>
                    <td className="text-right text-green-400 font-semibold">+10 pts</td>
                  </tr>
                  <tr className="border-b border-zinc-800">
                    <td className="py-3 font-medium text-orange-400">Hitos del Ciclo</td>
                    <td className="py-3 text-zinc-400">Metas mensuales alcanzadas</td>
                    <td className="text-right text-purple-400 font-semibold">15-120 pts</td>
                  </tr>
                  <tr>
                    <td className="py-3 font-medium text-cyan-400">Logros Permanentes</td>
                    <td className="py-3 text-zinc-400">Badges acumulados de por vida</td>
                    <td className="text-right text-purple-400 font-semibold">8-20 pts</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Volume Bonuses */}
        <Card className="bg-gradient-to-br from-emerald-900/60 to-emerald-950/80 border-emerald-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-emerald-300">
              <Coins className="h-6 w-6" />
              2. Bonos Económicos por Volumen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-zinc-300 text-sm">
              Al vender <span className="text-white font-semibold">10 membresías del mismo tipo</span> en un mes, 
              recibes un bono económico inmediato además de los puntos:
            </p>
            
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-slate-900/60 p-4 rounded-lg border border-amber-600/30 text-center">
                <span className="text-3xl">🥉</span>
                <h4 className="text-amber-600 font-bold mt-2">10 Bronce</h4>
                <p className="text-2xl font-bold text-white mt-1">₡12,000</p>
                <p className="text-zinc-400 text-xs mt-1">+ 30 pts ranking</p>
              </div>
              <div className="bg-slate-900/60 p-4 rounded-lg border border-slate-400/30 text-center">
                <span className="text-3xl">🥈</span>
                <h4 className="text-slate-400 font-bold mt-2">10 Plata</h4>
                <p className="text-2xl font-bold text-white mt-1">₡20,000</p>
                <p className="text-zinc-400 text-xs mt-1">+ 60 pts ranking</p>
              </div>
              <div className="bg-slate-900/60 p-4 rounded-lg border border-yellow-500/30 text-center">
                <span className="text-3xl">🥇</span>
                <h4 className="text-yellow-400 font-bold mt-2">10 Oro</h4>
                <p className="text-2xl font-bold text-white mt-1">₡40,000</p>
                <p className="text-zinc-400 text-xs mt-1">+ 120 pts ranking</p>
              </div>
            </div>
            
            <div className="bg-slate-800/50 p-3 rounded-lg border border-emerald-500/20">
              <p className="text-zinc-400 text-sm text-center">
                <Zap className="h-4 w-4 inline text-yellow-400 mr-1" />
                Estos bonos se acumulan: puedes ganar múltiples bonos en el mismo mes
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Permanent Achievements */}
        <Card className="bg-gradient-to-br from-purple-900/60 to-purple-950/80 border-purple-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-purple-300">
              <Award className="h-6 w-6" />
              3. Logros Permanentes (Nunca se pierden)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-zinc-300 text-sm">
              Estos logros se acumulan de por vida y <span className="text-white font-semibold">nunca se reinician</span>.
              Sus puntos suman permanentemente a tu ranking.
            </p>
            
            {/* Sales Permanent */}
            {permanentSales.length > 0 && (
              <div>
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Flame className="h-4 w-4 text-orange-400" />
                  Ventas Acumuladas
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-purple-500/30">
                        <th className="text-left py-2 text-purple-400">Badge</th>
                        <th className="text-left py-2 text-purple-400">Nombre</th>
                        <th className="text-left py-2 text-purple-400">Requisito</th>
                        <th className="text-right py-2 text-purple-400">Puntos</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                      {permanentSales.map((achievement) => (
                        <tr key={achievement.type} className="border-b border-zinc-800/50">
                          <td className="py-2 text-2xl">{achievement.badge}</td>
                          <td className="py-2 font-medium text-white">{achievement.name}</td>
                          <td className="py-2 text-zinc-400">{achievement.requirement}</td>
                          <td className="text-right py-2 text-green-400">+{achievement.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <Separator className="bg-purple-500/20" />

            {/* Redemptions Permanent */}
            {permanentRedemptions.length > 0 && (
              <div>
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Gift className="h-4 w-4 text-pink-400" />
                  Canjes Acumulados
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-purple-500/30">
                        <th className="text-left py-2 text-purple-400">Badge</th>
                        <th className="text-left py-2 text-purple-400">Nombre</th>
                        <th className="text-left py-2 text-purple-400">Requisito</th>
                        <th className="text-right py-2 text-purple-400">Puntos</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                      {permanentRedemptions.map((achievement) => (
                        <tr key={achievement.type} className="border-b border-zinc-800/50">
                          <td className="py-2 text-2xl">{achievement.badge}</td>
                          <td className="py-2 font-medium text-white">{achievement.name}</td>
                          <td className="py-2 text-zinc-400">{achievement.requirement}</td>
                          <td className="text-right py-2 text-green-400">+{achievement.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <Separator className="bg-purple-500/20" />

            {/* Savings Permanent */}
            {permanentSavings.length > 0 && (
              <div>
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <span className="text-lg">💰</span>
                  Ahorros Generados
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-purple-500/30">
                        <th className="text-left py-2 text-purple-400">Badge</th>
                        <th className="text-left py-2 text-purple-400">Nombre</th>
                        <th className="text-left py-2 text-purple-400">Requisito</th>
                        <th className="text-right py-2 text-purple-400">Puntos</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                      {permanentSavings.map((achievement) => (
                        <tr key={achievement.type} className="border-b border-zinc-800/50">
                          <td className="py-2 text-2xl">{achievement.badge}</td>
                          <td className="py-2 font-medium text-white">{achievement.name}</td>
                          <td className="py-2 text-zinc-400">{achievement.requirement}</td>
                          <td className="text-right py-2 text-green-400">+{achievement.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <Separator className="bg-purple-500/20" />

            {/* Training Permanent */}
            {permanentTraining.length > 0 && (
              <div>
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <GraduationCap className="h-4 w-4 text-blue-400" />
                  Entrenamiento
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-purple-500/30">
                        <th className="text-left py-2 text-purple-400">Badge</th>
                        <th className="text-left py-2 text-purple-400">Nombre</th>
                        <th className="text-left py-2 text-purple-400">Requisito</th>
                        <th className="text-right py-2 text-purple-400">Puntos</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                      {permanentTraining.map((achievement) => (
                        <tr key={achievement.type} className="border-b border-zinc-800/50">
                          <td className="py-2 text-2xl">{achievement.badge}</td>
                          <td className="py-2 font-medium text-white">{achievement.name}</td>
                          <td className="py-2 text-zinc-400">{achievement.requirement}</td>
                          <td className="text-right py-2 text-green-400">+{achievement.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 4: Cycle Milestones */}
        <Card className="bg-gradient-to-br from-orange-900/60 to-orange-950/80 border-orange-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-orange-300">
              <TrendingUp className="h-6 w-6" />
              4. Hitos del Ciclo (Se reinician cada mes)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-zinc-300 text-sm">
              Estos hitos se <span className="text-white font-semibold">reinician al inicio de cada mes</span>.
              ¡Compite cada ciclo para ganarlos nuevamente!
            </p>
            
            {/* Sales Cycle */}
            {cycleSales.length > 0 && (
              <div>
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Flame className="h-4 w-4 text-orange-400" />
                  Metas de Ventas
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-orange-500/30">
                        <th className="text-left py-2 text-orange-400">Badge</th>
                        <th className="text-left py-2 text-orange-400">Nombre</th>
                        <th className="text-left py-2 text-orange-400">Requisito</th>
                        <th className="text-right py-2 text-orange-400">Puntos</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                      {cycleSales.map((achievement) => (
                        <tr key={achievement.type} className="border-b border-zinc-800/50">
                          <td className="py-2 text-2xl">{achievement.badge}</td>
                          <td className="py-2 font-medium text-white">{achievement.name}</td>
                          <td className="py-2 text-zinc-400">{achievement.requirement}</td>
                          <td className="text-right py-2 text-green-400">+{achievement.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <Separator className="bg-orange-500/20" />

            {/* Redemptions Cycle */}
            {cycleRedemptions.length > 0 && (
              <div>
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Gift className="h-4 w-4 text-pink-400" />
                  Metas de Canjes
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-orange-500/30">
                        <th className="text-left py-2 text-orange-400">Badge</th>
                        <th className="text-left py-2 text-orange-400">Nombre</th>
                        <th className="text-left py-2 text-orange-400">Requisito</th>
                        <th className="text-right py-2 text-orange-400">Puntos</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                      {cycleRedemptions.map((achievement) => (
                        <tr key={achievement.type} className="border-b border-zinc-800/50">
                          <td className="py-2 text-2xl">{achievement.badge}</td>
                          <td className="py-2 font-medium text-white">{achievement.name}</td>
                          <td className="py-2 text-zinc-400">{achievement.requirement}</td>
                          <td className="text-right py-2 text-green-400">+{achievement.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <Separator className="bg-orange-500/20" />
            {cycleSavings.length > 0 && (
              <div>
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <span className="text-lg">💎</span>
                  Metas de Ahorros
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-orange-500/30">
                        <th className="text-left py-2 text-orange-400">Badge</th>
                        <th className="text-left py-2 text-orange-400">Nombre</th>
                        <th className="text-left py-2 text-orange-400">Requisito</th>
                        <th className="text-right py-2 text-orange-400">Puntos</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                      {cycleSavings.map((achievement) => (
                        <tr key={achievement.type} className="border-b border-zinc-800/50">
                          <td className="py-2 text-2xl">{achievement.badge}</td>
                          <td className="py-2 font-medium text-white">{achievement.name}</td>
                          <td className="py-2 text-zinc-400">{achievement.requirement}</td>
                          <td className="text-right py-2 text-green-400">+{achievement.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <Separator className="bg-orange-500/20" />

            {/* Ranking Cycle */}
            {cycleRanking.length > 0 && (
              <div>
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-yellow-400" />
                  Posiciones del Ranking
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-orange-500/30">
                        <th className="text-left py-2 text-orange-400">Badge</th>
                        <th className="text-left py-2 text-orange-400">Nombre</th>
                        <th className="text-left py-2 text-orange-400">Requisito</th>
                        <th className="text-right py-2 text-orange-400">Puntos</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                      {cycleRanking.map((achievement) => (
                        <tr key={achievement.type} className="border-b border-zinc-800/50">
                          <td className="py-2 text-2xl">{achievement.badge}</td>
                          <td className="py-2 font-medium text-white">{achievement.name}</td>
                          <td className="py-2 text-zinc-400">{achievement.requirement}</td>
                          <td className="text-right py-2 text-green-400">+{achievement.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <Separator className="bg-orange-500/20" />

            {/* Special Cycle */}
            {cycleSpecial.length > 0 && (
              <div>
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Star className="h-4 w-4 text-cyan-400" />
                  Logros Especiales
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-orange-500/30">
                        <th className="text-left py-2 text-orange-400">Badge</th>
                        <th className="text-left py-2 text-orange-400">Nombre</th>
                        <th className="text-left py-2 text-orange-400">Requisito</th>
                        <th className="text-right py-2 text-orange-400">Puntos</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                      {cycleSpecial.map((achievement) => (
                        <tr key={achievement.type} className="border-b border-zinc-800/50">
                          <td className="py-2 text-2xl">{achievement.badge}</td>
                          <td className="py-2 font-medium text-white">{achievement.name}</td>
                          <td className="py-2 text-zinc-400">{achievement.requirement}</td>
                          <td className="text-right py-2 text-green-400">+{achievement.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 5: Training */}
        <Card className="bg-gradient-to-br from-green-900/60 to-green-950/80 border-green-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-green-300">
              <GraduationCap className="h-6 w-6" />
              5. Entrenamiento Obligatorio
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-900/60 p-4 rounded-lg border border-green-500/20">
                <h4 className="text-white font-semibold mb-2">Estructura del Curso</h4>
                <ul className="space-y-2 text-zinc-300 text-sm">
                  <li className="flex items-center gap-2">
                    <span className="text-green-400">✓</span>
                    6 módulos de formación
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-400">✓</span>
                    Cada módulo tiene un quiz final
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-400">✓</span>
                    Se requiere 80% para aprobar
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-400">✓</span>
                    Avance secuencial obligatorio
                  </li>
                </ul>
              </div>
              
              <div className="bg-slate-900/60 p-4 rounded-lg border border-yellow-500/20">
                <h4 className="text-white font-semibold mb-2">Recompensas al Completar</h4>
                <ul className="space-y-2 text-zinc-300 text-sm">
                  <li className="flex items-center gap-2">
                    <span className="text-2xl">🎓</span>
                    Badge "Experto VillaTech"
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-yellow-400 font-bold">+15</span>
                    Puntos permanentes en ranking
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-2xl">🎉</span>
                    Celebración con confetti
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 6: Monthly Prizes */}
        <Card className="bg-gradient-to-br from-yellow-900/60 to-yellow-950/80 border-yellow-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-yellow-300">
              <Gift className="h-6 w-6" />
              6. Premios Mensuales por Ranking
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            
            {/* Default Prize Values */}
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-yellow-600/20 to-yellow-900/40 p-4 rounded-lg border border-yellow-500/40 text-center">
                <span className="text-4xl">🥇</span>
                <h4 className="text-yellow-400 font-bold mt-2">1er Lugar</h4>
                <p className="text-3xl font-bold text-white mt-1">₡40,000</p>
              </div>
              <div className="bg-gradient-to-br from-slate-400/20 to-slate-600/40 p-4 rounded-lg border border-slate-400/40 text-center">
                <span className="text-4xl">🥈</span>
                <h4 className="text-slate-300 font-bold mt-2">2do Lugar</h4>
                <p className="text-3xl font-bold text-white mt-1">₡30,000</p>
              </div>
              <div className="bg-gradient-to-br from-amber-700/20 to-amber-900/40 p-4 rounded-lg border border-amber-600/40 text-center">
                <span className="text-4xl">🥉</span>
                <h4 className="text-amber-500 font-bold mt-2">3er Lugar</h4>
                <p className="text-3xl font-bold text-white mt-1">₡20,000</p>
              </div>
            </div>

            <div className="bg-slate-800/50 p-3 rounded-lg border border-yellow-500/20">
              <p className="text-zinc-400 text-sm text-center">
                * Los valores pueden variar según el mes. Consulta la pestaña "Premios" para ver el premio actual.
              </p>
            </div>

            <Separator className="bg-yellow-500/20" />

            <div>
              <h4 className="text-white font-semibold mb-3">Ciclo del Premio</h4>
              <div className="flex flex-col md:flex-row gap-2 md:gap-4">
                <div className="flex items-center gap-2 bg-slate-900/60 p-3 rounded-lg flex-1">
                  <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                  <span className="text-zinc-300 text-sm">Se publica el premio del mes</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-900/60 p-3 rounded-lg flex-1">
                  <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                  <span className="text-zinc-300 text-sm">Vendedores compiten</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-900/60 p-3 rounded-lg flex-1">
                  <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">3</span>
                  <span className="text-zinc-300 text-sm">Se asigna ganador</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-900/60 p-3 rounded-lg flex-1">
                  <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">4</span>
                  <span className="text-zinc-300 text-sm">Reclamo con código</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 7: Evaluation Periods */}
        <Card className="bg-gradient-to-br from-cyan-900/60 to-cyan-950/80 border-cyan-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-cyan-300">
              <Calendar className="h-6 w-6" />
              7. Períodos de Evaluación
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-slate-900/60 p-4 rounded-lg border border-cyan-500/20 text-center">
                <h4 className="text-white font-semibold mb-2">Semanal</h4>
                <p className="text-zinc-400 text-sm">Vista rápida del rendimiento actual</p>
              </div>
              <div className="bg-slate-900/60 p-4 rounded-lg border border-yellow-500/30 text-center">
                <Crown className="h-6 w-6 text-yellow-400 mx-auto mb-2" />
                <h4 className="text-white font-semibold mb-2">Mensual</h4>
                <p className="text-zinc-400 text-sm">Período principal de competencia</p>
              </div>
              <div className="bg-slate-900/60 p-4 rounded-lg border border-cyan-500/20 text-center">
                <h4 className="text-white font-semibold mb-2">Histórico</h4>
                <p className="text-zinc-400 text-sm">Ranking de todo el tiempo</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 8: Participating Stores */}
        <Card className="bg-gradient-to-br from-slate-800/60 to-slate-900/80 border-slate-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-white">
              <MapPin className="h-6 w-6" />
              8. Tiendas Participantes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
              {['Plaza del Sol', 'Lincoln Plaza', 'Multiplaza', 'Otros puntos'].map((store) => (
                <div key={store} className="bg-slate-900/60 p-3 rounded-lg border border-slate-600/30 text-center">
                  <p className="text-white font-medium">{store}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center pt-6 border-t border-blue-500/30 space-y-2">
          <p className="text-zinc-400 text-sm">
            Documento oficial del Programa de Incentivos VillaTech
          </p>
          <p className="text-zinc-500 text-xs">
            Última actualización: {new Date().toLocaleDateString('es-CR', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
          <p className="text-zinc-600 text-xs">
            © {new Date().getFullYear()} VillaTech. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default IncentiveProgramDocument;
